import AnimatedIconAi from '@assets/images/icons/octopus/ai-animated.gif'
import StarsAiIcon from '@assets/images/icons/stars.svg?react'
import { MODAL_NONE } from '@components/App/Modal/constants'
import { ROUTE_AI_SITEMAP_NEW } from '@components/Router/constants'
import { ReplayableIcon } from '@lib/components/ReplayableIcon'
import { Button } from '@octopus-do/octopus-storybook'
import { managerActions } from '@store/reducers/manager/actions'
import { openModal } from '@storeOld/reducers/ui'
import type React from 'react'
import { type KeyboardEvent, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import { useGenerateProject } from '@/hooks/ai'
import css from './GenerateNewProjectModal.module.scss'

type Props = {
  closeButton: React.ReactNode
  teamId?: string
  folderId?: string
  defaultTheme?: string
  // Opened from the AI menu of an open project (OCT-5102): the sitemap lands as a new
  // section of that project, the way File → Import does, instead of starting a new one.
  intoOpenProject?: boolean
}

const buildSearch = (
  generationTheme: string,
  teamId?: string,
  folderId?: string
) => {
  const search = new URLSearchParams({
    generationTheme,
  })

  if (teamId) {
    search.set('teamId', teamId)
  }
  if (folderId) {
    search.set('folderId', folderId)
  }

  return search
}

const GenerateNewProjectModal: React.FC<Props> = ({
  closeButton,
  teamId,
  folderId,
  defaultTheme,
  intoOpenProject = false,
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null)

  const navigate = useNavigate()
  const dispatch = useDispatch()

  const generateIntoSection = useGenerateProject(
    intoOpenProject ? { newSection: true } : undefined
  )

  const [generationTheme, setGenerationTheme] = useState<string>(
    defaultTheme || ''
  )

  const handleSubmit = async () => {
    const formattedGenerationTheme = generationTheme.trim()

    if (!formattedGenerationTheme) {
      return
    }

    if (intoOpenProject) {
      // No navigation and no resetProject: the project stays open and the pages stream
      // straight into it.
      dispatch(openModal({ type: MODAL_NONE }))
      generateIntoSection(formattedGenerationTheme)
      return
    }

    const search = buildSearch(formattedGenerationTheme, teamId, folderId)

    dispatch(openModal({ type: MODAL_NONE }))
    dispatch(managerActions.resetProject())
    navigate(`${ROUTE_AI_SITEMAP_NEW}?${search}&`)
  }

  const onFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (intoOpenProject) {
      sendGtmEvent(
        GtmTriggerName.AI_SECTION_GENERATE,
        'Click to generate a section into the open project from AI modal'
      )
    } else {
      sendGtmEvent(
        GtmTriggerName.AI_PROJECT_GENERATE,
        'Click to generate project from AI modal'
      )
    }
    handleSubmit()
  }

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.metaKey && e.key === 'Enter') {
      buttonRef.current?.click()
    }
  }

  return (
    <div className={css.modal}>
      <div className={css.heading}>
        <div className={css.iconContainer}>
          <ReplayableIcon
            src={AnimatedIconAi}
            className={css.aiLogo}
            alt="AI Logo"
          />
        </div>
        <div className={css.title}>AI powered sitemap generation</div>
        {closeButton}
      </div>

      <form onSubmit={onFormSubmit}>
        <div className={css.body}>
          <textarea
            autoFocus
            className={css.textarea}
            onChange={(e) => setGenerationTheme(e.target.value)}
            value={generationTheme}
            placeholder="A few words about your website..."
            onKeyDown={onKeyDown}
          />
        </div>

        <div className={css.footer}>
          <Button
            ref={buttonRef}
            className={css.wideButton}
            type="submit"
            tone="ai"
            label="Generate"
            left={StarsAiIcon}
            disabled={!generationTheme.trim()}
            data-testid="create_project"
          />

          <div className={css.terms}>
            By using, you are agreeing to our{' '}
            <a
              href="https://octopus.do/sitemap/terms"
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms
            </a>
            . Generated content may be inaccurate or false.
          </div>
        </div>
      </form>
    </div>
  )
}

export default GenerateNewProjectModal
